import { type MeResponse, parseScope } from '@platform/contracts';

export const SCOPE_STORAGE_KEY = 'etw.scope';

export interface ScopeOption {
  value: string;
  label: string;
  kind: 'all' | 'client' | 'tenant';
  /** Nombre del cliente (para agrupar en el selector). */
  group?: string;
  /** Empresas: slug (color del monograma) y logo. */
  slug?: string;
  logoUrl?: string | null;
}

/**
 * Opciones del selector: "Todas", y por cada cliente "Todas de <cliente>" + sus empresas.
 * "Todas de <cliente>" sólo aparece si el cliente tiene más de una empresa visible.
 */
export function buildScopeOptions(me: Pick<MeResponse, 'clients'>): ScopeOption[] {
  const options: ScopeOption[] = [{ value: 'all', label: 'Todas las empresas', kind: 'all' }];
  for (const client of me.clients) {
    if (client.tenants.length > 1) {
      options.push({
        value: `client:${client.slug}`,
        label: `Todas de ${client.name}`,
        kind: 'client',
        group: client.name,
      });
    }
    for (const t of client.tenants) {
      options.push({
        value: `tenant:${t.slug}`,
        label: t.name,
        kind: 'tenant',
        group: client.name,
        slug: t.slug,
        logoUrl: t.logoUrl ?? null,
      });
    }
  }
  return options;
}

export function isAccessibleScope(
  me: Pick<MeResponse, 'clients'>,
  scope: string | undefined,
): boolean {
  if (!scope) return false;
  const parsed = parseScope(scope);
  if (!parsed) return false;
  if (parsed.kind === 'all') return true;
  if (parsed.kind === 'client')
    return me.clients.some((c) => c.slug === parsed.ref && c.tenants.length > 0);
  return me.clients.some((c) => c.tenants.some((t) => t.slug === parsed.ref));
}

export function readStoredScope(): string | undefined {
  try {
    return localStorage.getItem(SCOPE_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function storeScope(scope: string): void {
  try {
    localStorage.setItem(SCOPE_STORAGE_KEY, scope);
  } catch {
    // modo privado / storage bloqueado: la selección sigue viviendo en la URL
  }
}

/** URL → si no vale, lo guardado → si no vale, "all". Nunca devuelve un alcance inaccesible. */
export function effectiveScope(
  me: Pick<MeResponse, 'clients'>,
  fromUrl: string | undefined,
): string {
  if (isAccessibleScope(me, fromUrl) && fromUrl) return fromUrl;
  const stored = readStoredScope();
  if (isAccessibleScope(me, stored) && stored) return stored;
  return 'all';
}

/** Empresas incluidas en un alcance (para pantallas que necesitan una empresa concreta). */
export function tenantsInScope(me: Pick<MeResponse, 'clients'>, scope: string) {
  const parsed = parseScope(scope);
  const all = me.clients.flatMap((c) =>
    c.tenants.map((t) => ({ ...t, clientId: c.id, clientSlug: c.slug })),
  );
  if (!parsed || parsed.kind === 'all') return all;
  if (parsed.kind === 'client') return all.filter((t) => t.clientSlug === parsed.ref);
  return all.filter((t) => t.slug === parsed.ref);
}
